import { runMediaSave, type MediaSaveLifecycle } from './mediaSaveLifecycle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
    View,
    StyleSheet,
    TouchableOpacity,
    Text,
    Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../config/theme';
import { ChecklistMarkFieldLogger } from '../../utils/logger';

interface MarkPadProps extends MediaSaveLifecycle {
  backgroundUri: string;
  onOK: (dataUrl: string) => void;
  onClose: () => void;
}

/**
 * WebMarkPad – componente de marcação para web usando react-signature-canvas
 * Apenas captura o desenho e retorna via onOK, seguindo padrão do SignaturePad
 */
const WebMarkPad: React.FC<MarkPadProps> = ({ backgroundUri, onOK, onClose, onSaveStart, onSaveCancel, onSaveError, onDrawingEvent }) => {
  const MAX_EXPORT_WIDTH = 1280;
  const EXPORT_QUALITY = 0.75;
  const drawingStarted = useRef(false);
  const noteDrawing = () => { if (!drawingStarted.current) { drawingStarted.current = true; onDrawingEvent?.('drawing_started'); } };
  const sigRef = useRef<SignatureCanvas>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 400 });
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number }>({ width: 600, height: 400 });
  const [isEmpty, setIsEmpty] = useState(true);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [bgImageObj, setBgImageObj] = useState<HTMLImageElement | null>(null);

  // Atualiza layout quando viewport muda
  const updateLayout = useCallback(() => {
    const { width, height } = Dimensions.get('window');
    const w = Math.min(width - 80, 800);
    const h = Math.min(height - 200, 600);
    setCanvasSize({ width: w, height: h });
  }, []);

  useEffect(() => {
    updateLayout();
    const sub = Dimensions.addEventListener('change', updateLayout);
    return () => sub.remove();
  }, [updateLayout]);

  // Carrega imagem de fundo
  useEffect(() => {
    if (backgroundUri) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setBgImageObj(img);
        setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });

        // Ajusta tamanho de exibição mantendo proporção
        const maxW = Math.min(window.innerWidth - 80, 800);
        const maxH = Math.min(window.innerHeight - 200, 600);
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        const aspect = w / h;
        if (w > maxW) {
          w = maxW;
          h = w / aspect;
        }
        if (h > maxH) {
          h = maxH;
          w = h * aspect;
        }
        setCanvasSize({ width: w, height: h });

        setBackgroundLoaded(true);
        ChecklistMarkFieldLogger.Main.info(`[WebMarkPad] Imagem de fundo carregada. natural=${img.naturalWidth}x${img.naturalHeight} display=${w}x${h}`);
      };
      img.onerror = () => {
        ChecklistMarkFieldLogger.Main.error('[WebMarkPad] Erro ao carregar imagem de fundo');
      };
      img.src = backgroundUri;
    }
  }, [backgroundUri]);

  const handleBegin = () => {
    noteDrawing();
    setIsEmpty(false);
  };

  const handleClear = () => {
    onDrawingEvent?.('drawing_cleared');
    ChecklistMarkFieldLogger.Main.info('[WebMarkPad] Limpando marcação');
    sigRef.current?.clear();
    setIsEmpty(true);
  };

  const handleSave = () => {
    if (!sigRef.current) throw new Error('Canvas indisponível ao salvar.');

    ChecklistMarkFieldLogger.Main.info('[WebMarkPad] Iniciando captura da marcação');

    // Canvas original da assinatura
    const originalCanvas = sigRef.current.getCanvas();
    
    // Verifica se há desenho
    const octx = originalCanvas.getContext('2d')!;
    const pixels = octx.getImageData(0, 0, originalCanvas.width, originalCanvas.height).data;
    const hasInk = (() => {
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] > 0) return true;
      }
      return false;
    })();

    if (!hasInk) {
            onDrawingEvent?.('drawing_empty'); onSaveCancel?.();
      alert('Marcação vazia. Desenhe antes de salvar.');
      return;
    }

    // Busca a imagem de fundo do DOM
    const backgroundImg = bgImageObj;
    if (!backgroundImg) {
      onSaveError?.(new Error('background_missing'));
      alert('Erro: imagem de fundo não carregada.');
      return;
    }

    // Cria canvas final combinando fundo + desenho
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = naturalSize.width;
    finalCanvas.height = naturalSize.height;
    const fctx = finalCanvas.getContext('2d')!;

    // Desenha fundo sem distorcer (preenchendo natural size)
    fctx.drawImage(backgroundImg, 0, 0, naturalSize.width, naturalSize.height);
    
    // Escala overlay para as dimensões naturais
    fctx.drawImage(originalCanvas, 0, 0, naturalSize.width, naturalSize.height);

    // Exporta mantendo proporção e reduzindo largura quando necessário
    let outputCanvas = finalCanvas;
    if (naturalSize.width > MAX_EXPORT_WIDTH) {
      const scale = MAX_EXPORT_WIDTH / naturalSize.width;
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = Math.round(naturalSize.width * scale);
      resizedCanvas.height = Math.round(naturalSize.height * scale);
      const rctx = resizedCanvas.getContext('2d')!;
      rctx.drawImage(finalCanvas, 0, 0, resizedCanvas.width, resizedCanvas.height);
      outputCanvas = resizedCanvas;
    }

    // Gera JPEG final para reduzir tamanho do arquivo
    const dataUrl = outputCanvas.toDataURL('image/jpeg', EXPORT_QUALITY);
    ChecklistMarkFieldLogger.Main.info('[WebMarkPad] Marcação capturada com sucesso');
    onOK(dataUrl);
  };

  return (
    <View style={styles.container}>
      {/* Cabeçalho */}
      <View style={styles.header}>
        <TouchableOpacity
          accessibilityLabel="Fechar marcação"
          accessibilityRole="button"
          onPress={onClose}
          style={styles.iconButton}
        >
          <Ionicons name="close" size={24} color={theme.colors.text.primary} />
        </TouchableOpacity>
        <Text style={styles.title}>Marcação</Text>
      </View>

      {/* Canvas */}
      <View style={styles.canvasWrapper}>
        {backgroundLoaded && (
          <div style={{ position: 'relative', width: canvasSize.width, height: canvasSize.height }}>
            {/* Imagem de fundo */}
            <img
              src={backgroundUri}
              alt="Fundo para marcação"
              crossOrigin="anonymous"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                zIndex: 0,
              }}
            />
            {/* Canvas de desenho */}
            <SignatureCanvas
              ref={sigRef}
              onBegin={handleBegin}
              penColor="#ff0000"
              backgroundColor="rgba(0,0,0,0)"
              clearOnResize={false}
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                style: {
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  zIndex: 1,
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: theme.borderRadius.xs,
                  touchAction: 'none',
                },
              }}
            />
          </div>
        )}
      </View>

      {/* Botões */}
      <View style={styles.footer}>
        <TouchableOpacity
          accessibilityLabel="Limpar marcação"
          accessibilityRole="button"
          onPress={handleClear}
          style={styles.button}
          disabled={isEmpty}
        >
          <Text style={styles.buttonText}>Limpar</Text>
        </TouchableOpacity>

        <TouchableOpacity
          accessibilityLabel="Salvar marcação"
          accessibilityRole="button"
          onPress={() => runMediaSave(handleSave, { onSaveStart, onSaveError })}
          style={[styles.button, styles.saveButton, isEmpty && styles.disabledButton]}
          disabled={isEmpty}
        >
          <Text style={[styles.buttonText, styles.saveButtonText]}>Salvar</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  iconButton: {
    marginRight: theme.spacing.sm,
  },
  title: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  canvasWrapper: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: theme.spacing.md,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing.md,
  },
  button: {
    flex: 1,
    paddingVertical: theme.spacing.sm,
    marginHorizontal: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.dark,
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    ...theme.shadows.sm,
  },
  saveButton: {
    backgroundColor: theme.colors.primary.dark,
    borderColor: theme.colors.primary.dark,
  },
  buttonText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.medium as any,
  },
  saveButtonText: {
    color: theme.colors.text.inverse,
  },
  disabledButton: {
    opacity: 0.5,
  },
});

export default WebMarkPad; 
